import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Textarea } from "@/client/components/ui/textarea";
import type { Story } from "@/client/db";

interface ProjectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  story?: Story | null;
  onSubmit: (data: { name: string; synopsis?: string }) => void;
  isLoading?: boolean;
}

export function ProjectForm({
  open,
  onOpenChange,
  story,
  onSubmit,
  isLoading,
}: ProjectFormProps) {
  const [name, setName] = useState("");
  // The story's synopsis, the same one its details show and the AI reads
  const [synopsis, setSynopsis] = useState("");

  useEffect(() => {
    if (story) {
      setName(story.name);
      setSynopsis(story.synopsis || "");
    } else {
      setName("");
      setSynopsis("");
    }
  }, [story, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    // Editing can clear it; a new story without one simply has none
    onSubmit({ name: name.trim(), synopsis: story ? synopsis.trim() : synopsis.trim() || undefined });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{story ? "Edit story" : "New story"}</DialogTitle>
            <DialogDescription>
              {story
                ? "Change its name or what it's about."
                : "A new visual novel. Only the name is needed to start."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Visual Novel"
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="synopsis">What's it about? (optional)</Label>
              <Textarea
                id="synopsis"
                value={synopsis}
                onChange={(e) => setSynopsis(e.target.value)}
                placeholder="The whole story in a sentence or two."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isLoading}>
              {isLoading ? "Saving..." : story ? "Save changes" : "Create story"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
